import { GenericCustomFieldInputProps } from './CustomFieldInput';
import React, { useState, useCallback } from 'react';
import { Link, TextField, Typography } from '@mui/material';
import { useIntl } from 'react-intl';
import { useFindIndividualsQuery } from 'gql';
import { useFormContext } from 'react-hook-form';
import { Link as RouterLink } from 'react-router-dom';
import { getTenantIdentifier } from 'utils/tenantUtils';
import _ from 'lodash';

interface NicknameInputProps extends GenericCustomFieldInputProps {
  individualId?: number;
}

export const NicknameInput: React.FC<NicknameInputProps> = (props) => {
  const { formatMessage } = useIntl();
  const { value, onChange, helperText, error } = props;
  const [nicknameToCheck, setNicknameToCheck] = useState<string | null>(null);

  const { getValues } = useFormContext<{ id?: number }>();
  const currentIndividualId = getValues('id');

  const { data: existingIndividuals } = useFindIndividualsQuery(
    {
      pageSize: 5,
      skip: 0,
      input: {},
      searchTerm: nicknameToCheck ?? ''
    },
    {
      enabled: Boolean(nicknameToCheck && nicknameToCheck.trim().length > 0)
    }
  );

  const matchingIndividuals = existingIndividuals?.searchIndividuals?.items?.filter(
    (individual) => {
      if (currentIndividualId && individual.id === currentIndividualId) {
        return false;
      }

      return _.deburr(individual.nickName?.toLowerCase()) === _.deburr(nicknameToCheck?.toLowerCase());
    }
  );

  const hasDuplicates = matchingIndividuals && matchingIndividuals.length > 0;
  const duplicateIndividual = matchingIndividuals?.[0];

  const handleBlur = useCallback(() => {
    if (value && typeof value === 'string' && value.trim().length > 0) {
      setNicknameToCheck(value.trim());
    } else {
      setNicknameToCheck(null);
    }
  }, [value]);

  const renderDuplicateWarning = () => {
    if (!duplicateIndividual) return null;

    return (
      <Typography component="span" variant="caption" color="warning.main">
        {formatMessage({ id: 'An individual with this nickname might already exist' })}
        {' - '}
        <Link
          component={RouterLink}
          to={`/${getTenantIdentifier()}/individuals?editIndividualId=${duplicateIndividual.id}`}
          target="_blank"
          color="warning.main"
          sx={{ textDecoration: 'underline' }}
        >
          {formatMessage({ id: 'View' })}
        </Link>
      </Typography>
    );
  };

  return (
    <TextField
      {...props}
      fullWidth
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      onBlur={handleBlur}
      helperText={
        hasDuplicates && !error
          ? renderDuplicateWarning()
          : (error ? helperText : helperText)
      }
    />
  );
};
